/**
 * Tank registry PR 2 — "Import tanks" in the Operator tanks card: the same CSV / XLSX import the
 * operator admin has on Tank setup, against the platform route for the company. Choose a file,
 * Preview (nothing is written), then "Import N" sends the SAME file again with ?apply=true. The
 * server re-reads the file and the registry on apply, so a row can turn into "Not imported" at
 * write time; the apply's own rows replace the preview's. The file format (header aliases, rules)
 * is the API's: services/operator-tank-import.ts.
 */
import * as React from 'react';
import { apiErrorText, useApiOpts } from '@lodex-pro/web-shared';
import type {
  TankImportResponse,
  TankImportRowStatus,
  TankTagSystem,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { Button } from 'src/components/ui/button';
import { Pill, labelStyle, subText } from '../../ui/bits';
import { importCompanyTanks, useRefreshCompanyTanks } from './tank-registry-hooks';

const TEMPLATE_HEADERS = [
  'Tank Name',
  // 13.8 (D-22b): the tank's producer tank ID; the import matches by it first.
  'Producer Tank ID',
  'API Number',
  'Site',
  'Battery',
  'Capacity (bbl)',
  'Height (in)',
  'BBL/in',
  'Tank Type',
  'Catalog Type',
  'SCADA Tag',
  'MQTT Topic',
  'Tags',
] as const;
const TEMPLATE_CSV = `${TEMPLATE_HEADERS.join(',')}\r\n`;

const STATUS_LABEL: Record<TankImportRowStatus, string> = {
  NEW: 'New',
  UPDATE: 'Update',
  UNCHANGED: 'Unchanged',
  ERROR: 'Not imported',
};
const SYSTEM_LABEL: Record<TankTagSystem, string> = {
  SCADA: 'SCADA',
  MQTT: 'MQTT',
  OTHER: 'Other',
};
const primaryButton: React.CSSProperties = {
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const cell: React.CSSProperties = {
  padding: '4px',
  borderTop: '1px solid var(--color-border-secondary)',
  verticalAlign: 'top',
};

/** A link to a blank CSV template, built here (a Blob behind an object URL). */
function TemplateLink(): React.JSX.Element | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined;
    const u = URL.createObjectURL(new Blob([TEMPLATE_CSV], { type: 'text/csv' }));
    setUrl(u);
    return (): void => URL.revokeObjectURL(u);
  }, []);
  if (url === null) return null;
  return (
    <a href={url} download="tank-import-template.csv" style={{ color: 'var(--color-link)' }}>
      Download a template
    </a>
  );
}

export function OperatorTankImport({ tenantId }: { tenantId: string }): React.JSX.Element {
  const opts = useApiOpts();
  const refresh = useRefreshCompanyTanks(tenantId);
  const [file, setFile] = React.useState<File | null>(null);
  const [response, setResponse] = React.useState<TankImportResponse | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = async (apply: boolean): Promise<void> => {
    if (file === null) return;
    setBusy(true);
    setError(null);
    try {
      setResponse(await importCompanyTanks(opts, tenantId, file, apply));
      if (apply) refresh();
    } catch (err) {
      setError(apiErrorText(err, 'The file could not be read.'));
    } finally {
      setBusy(false);
    }
  };

  const s = response?.summary;
  const writable = s === undefined ? 0 : s.new + s.update;
  const result = response?.result;

  return (
    <div style={{ marginTop: '20px' }} data-testid="import-tanks">
      <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 6px' }}>Import tanks</h4>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 10px' }}>
        A CSV or XLSX file, one tank per row. A row whose producer tank ID matches one of the
        company&rsquo;s tanks updates it, else a row whose name matches a tank at the same well,
        site or battery; any other row adds a tank. The Tank ID column is the producer&rsquo;s own
        ID for the tank, not its name. Files made before October 2026 that put names there will
        store those names as producer tank IDs. Give exactly one of a well API number, a site or a
        battery. Blank cells keep what is on file. Several tags in one cell are separated by
        semicolons. The import never removes a tag. Nothing is saved until you import.{' '}
        <TemplateLink />
      </p>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          aria-label="Tank file"
          type="file"
          accept=".csv,.xlsx"
          onChange={(e): void => {
            setFile(e.target.files?.[0] ?? null);
            setResponse(null);
            setError(null);
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={file === null || busy}
          onClick={(): void => void run(false)}
        >
          Preview
        </Button>
      </div>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)', marginTop: '10px' }}>
          {error}
        </p>
      )}
      {response !== null && s !== undefined && (
        <div style={{ marginTop: '12px' }}>
          {result !== undefined && (
            <p role="status" style={{ fontSize: '13px', margin: '0 0 8px' }}>
              Imported: {result.created} new, {result.updated} updated, {result.tagsAdded} tags
              added.
            </p>
          )}
          <p style={{ fontSize: '13px', margin: '0 0 8px' }} data-testid="tank-import-summary">
            {s.new} new · {s.update} update · {s.unchanged} unchanged · {s.errors} not imported
          </p>
          {response.rows.length === 0 ? (
            <p style={{ ...subText, textTransform: 'none' }}>The file has no tank rows.</p>
          ) : (
            <table
              aria-label="Import rows"
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}
            >
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ ...labelStyle, padding: '4px' }}>Row</th>
                  <th style={{ ...labelStyle, padding: '4px' }}>Tank</th>
                  <th style={{ ...labelStyle, padding: '4px' }}>Result</th>
                  <th style={{ ...labelStyle, padding: '4px' }}>Where</th>
                  <th style={{ ...labelStyle, padding: '4px' }}>Changes</th>
                  <th style={{ ...labelStyle, padding: '4px' }}>Tags added</th>
                  <th style={{ ...labelStyle, padding: '4px' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {response.rows.map((r) => (
                  <tr key={r.rowNumber}>
                    <td style={cell}>{r.rowNumber}</td>
                    <td style={cell}>{r.name === '' ? '—' : r.name}</td>
                    <td style={cell}>
                      <Pill
                        label={STATUS_LABEL[r.status]}
                        tone={
                          r.status === 'ERROR'
                            ? 'danger'
                            : r.status === 'NEW'
                              ? 'positive'
                              : r.status === 'UPDATE'
                                ? 'accent'
                                : 'neutral'
                        }
                      />
                    </td>
                    <td style={cell}>{r.placement ?? '—'}</td>
                    <td style={cell}>{r.changes.length === 0 ? '—' : r.changes.join(', ')}</td>
                    <td style={cell}>
                      {r.addTags.length === 0
                        ? '—'
                        : r.addTags.map((t) => `${SYSTEM_LABEL[t.system]}: ${t.tag}`).join(', ')}
                    </td>
                    <td style={cell}>{r.detail ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!response.applied && (
            <div style={{ marginTop: '10px' }}>
              <Button
                type="button"
                style={primaryButton}
                disabled={busy || writable === 0}
                onClick={(): void => void run(true)}
              >
                {busy ? 'Importing…' : `Import ${String(writable)}`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
